/**
 * @docType shared
 * @category directive
 * @name Autosave
 * @example
 *   <form ng-submit="myControl.save()" autosave has-changes="myControl.hasChanges()" autosave-debounce="5000">...</form>
 * @description
 * Enables automatic saves on any form when the form is marked dirty.
 *
 * @param autosave {statement} - Evaluated when an autosave occurs, defaults to ng-submit.  This statement should return a promise or true to know when a save is successful
 * @param hasChanges {statement} - Evaluated to determine if there have been changes, defaults to `(form.$valid && form.$dirty)`
 * @param autosave-debounce {number} - Number of milliseconds to debounce saves, defaults to 2000 (2s)
 * @returns Directive Definition Object {object}
 */
export const autosaveComponentModule = angular
  .module('Q.components.forms.autosave', [])

  .directive('autosave', function ($timeout) {
    return {
      restrict: 'A',
      require: '^form',
      link(scope, elem, attr, formCtrl) {
        const debounce = parseInt(attr.autosaveDebounce || 2000, 10);
        const markErrors = !!attr.markErrors;
        let nextSave = -1;
        let delayedSaveTimer = false;

        scope.$watch(() => {
          if (!delayedSaveTimer && hasChanges()) _save();
        });

        scope.$on('$destroy', () => {
          if (delayedSaveTimer) {
            $timeout.cancel(delayedSaveTimer);
          }
        });

        function hasChanges() {
          let hasChanges =
            (attr.hasChanges && scope.$eval(attr.hasChanges)) ||
            (formCtrl.$valid && formCtrl.$dirty);
          if (markErrors && !formCtrl.$valid && formCtrl.$dirty) {
            formCtrl.$setSubmitted();
          }
          return hasChanges;
        }

        function _save() {
          let delay = 100;

          if (Date.now() < nextSave) {
            delay = nextSave - Date.now();
          }
          delayedSaveTimer = $timeout(function () {
            delayedSaveTimer = false;
            if (hasChanges()) _handleSave();
          }, delay);
        }

        function _handleSave() {
          nextSave = Date.now() + debounce;

          _cleanForm();

          const fnName = _getSaveAttr(attr);

          scope.$eval(fnName);
        }

        function _cleanForm() {
          if (formCtrl.$valid) {
            formCtrl.$setPristine();
            formCtrl.$setUntouched();
          }
        }
      },
    };
  });

function _getSaveAttr(attr) {
  let ret = false;
  ['autosave', 'ngSubmit'].some(function (name) {
    if (attr[name] && angular.isString(attr[name]) && attr[name].length > 1) {
      ret = attr[name];
      return true;
    }
  });
  if (!ret) throw new Error('Unable to find save function');
  return ret;
}
